// ===========================================
// Record Payment Dialog
// ===========================================
// Dialog for recording manual payments on invoices, and for amending one
// (pass `payment`) when an admin made a mistake.
//
// #642 (md/plan/642.md D1, D4):
// - No cap: an amount above the balance is recorded in full, and the recorder chooses what
//   happens to the extra on a confirm step: Credit the family (default) or Refund due (only with
//   the Refunds permission). The server refuses an overpayment with no choice (409
//   OVERPAYMENT_NEEDS_CHOICE, e.g. the balance moved since the dialog opened); that opens the
//   same step with the server's figures.
// - Amending changes the reference and notes only. To change the amount, method or date, the
//   payment is voided and recorded again.

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Box,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useRecordPayment, useUpdatePayment } from '../../hooks/useInvoices';
import {
  Invoice,
  Payment,
  RecordPaymentInput,
  UpdatePaymentInput,
} from '../../api/invoices.api';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { usePermissions } from '../../hooks/usePermissions';
import { schoolTodayUtcMidnight, toUTCDateKey } from '../../utils/dateUtils';
import { formatRecordedAt, PAYMENT_METHOD_LABELS } from '../../utils/invoiceDisplay';
import { apiErrorCode, apiErrorDetails, apiErrorMessage } from '../../utils/apiError';

interface RecordPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  /** When set, the dialog amends this (manual) payment instead of recording a new one. */
  payment?: Payment | null;
}

type RecordableMethod = RecordPaymentInput['method'];

const PAYMENT_METHODS: { value: RecordableMethod; label: string }[] = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Cheque' },
  { value: 'OTHER', label: 'Other' },
];

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(num);
}

/** Money compared in whole cents, never in floating dollars. */
function toCents(value: string | number): number {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(num) ? Math.round(num * 100) : 0;
}

export default function RecordPaymentDialog({
  open,
  onClose,
  invoice,
  payment = null,
}: RecordPaymentDialogProps) {
  const isEdit = payment !== null;
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<RecordableMethod>('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [paidAt, setPaidAt] = useState('');
  // #642: the confirm step for an amount above the balance.
  const [step, setStep] = useState<'form' | 'excess'>('form');
  const [excessAction, setExcessAction] = useState<'CREDIT' | 'REFUND_DUE'>('CREDIT');
  const [excessNote, setExcessNote] = useState('');
  /** The server's figures when it refused (the balance moved since the dialog opened). */
  const [serverExcess, setServerExcess] = useState<{ remaining: string; excess: string } | null>(null);

  const timeZone = useSchoolTimezone();
  const todayKey = toUTCDateKey(schoolTodayUtcMidnight(timeZone));
  const { can } = usePermissions();
  const mayRefund = can('operations_refunds', 'write');

  const recordMutation = useRecordPayment();
  const updateMutation = useUpdatePayment();
  const mutation = isEdit ? updateMutation : recordMutation;

  const remainingCents = invoice ? toCents(invoice.total) - toCents(invoice.amountPaid) : 0;

  // Prefill when the dialog opens: the payment being amended, or the remaining
  // balance and the school's today for a new one.
  useEffect(() => {
    if (!open || !invoice) return;
    if (payment) {
      setAmount(parseFloat(payment.amount).toFixed(2));
      setMethod(payment.method as RecordableMethod);
      setReference(payment.reference ?? '');
      setNotes(payment.notes ?? '');
      // The school-local day the stored instant falls on
      setPaidAt(toUTCDateKey(schoolTodayUtcMidnight(timeZone, new Date(payment.paidAt))));
    } else {
      setAmount((remainingCents / 100).toFixed(2));
      setPaidAt(todayKey);
    }
  }, [open, invoice, payment, remainingCents, todayKey, timeZone]);

  const amountCents = toCents(amount);
  const excessCents = Math.max(0, amountCents - remainingCents);
  const shortCents = Math.max(0, remainingCents - amountCents);

  const handleClose = () => {
    setAmount('');
    setMethod('BANK_TRANSFER');
    setReference('');
    setNotes('');
    setPaidAt('');
    setStep('form');
    setExcessAction('CREDIT');
    setExcessNote('');
    setServerExcess(null);
    recordMutation.reset();
    updateMutation.reset();
    onClose();
  };

  const submitRecord = async (choice?: 'CREDIT' | 'REFUND_DUE') => {
    if (!invoice) return;
    const data: RecordPaymentInput = {
      amount: amountCents / 100,
      method,
      reference: reference || undefined,
      notes: notes || undefined,
      paidAt,
      ...(choice ? { excessAction: choice } : {}),
      ...(choice === 'REFUND_DUE' && excessNote.trim() ? { excessNote: excessNote.trim() } : {}),
    };
    try {
      await recordMutation.mutateAsync({ id: invoice.id, data });
      handleClose();
    } catch (error) {
      if (apiErrorCode(error) === 'OVERPAYMENT_NEEDS_CHOICE') {
        // The balance moved since the dialog opened: ask, with the server's figures.
        const details = apiErrorDetails<{ remaining?: string; excess?: string }>(error);
        setServerExcess({ remaining: details?.remaining ?? '0', excess: details?.excess ?? '0' });
        recordMutation.reset();
        setStep('excess');
      }
      // Anything else: shown in the dialog's Alert and the snackbar; stay open.
    }
  };

  const submitEdit = async () => {
    if (!invoice || !payment) return;
    // '' clears reference/notes on an amend; the money fields are never sent.
    const data: UpdatePaymentInput = { reference, notes };
    try {
      await updateMutation.mutateAsync({ paymentId: payment.id, invoiceId: invoice.id, data });
      handleClose();
    } catch {
      // Shown in the dialog's Alert (mutation.isError) and the snackbar; stay open
    }
  };

  const handlePrimary = async () => {
    if (isEdit) return submitEdit();
    if (step === 'form' && excessCents > 0) {
      setStep('excess');
      return;
    }
    return submitRecord(step === 'excess' ? excessAction : undefined);
  };

  const isAmountValid = amountCents > 0;
  // YYYY-MM-DD keys compare correctly as strings
  const isDateValid = /^\d{4}-\d{2}-\d{2}$/.test(paidAt) && paidAt <= todayKey;

  // #372: the first ACTIVE late fee added (school day) AFTER the chosen payment day, as dd/mm/yyyy.
  const lateFeeAfterPayment = (() => {
    if (!isDateValid || !invoice?.InvoiceLateFee) return null;
    const later = invoice.InvoiceLateFee.filter((fee) => !fee.waivedAt)
      .map((fee) => toUTCDateKey(schoolTodayUtcMidnight(timeZone, new Date(fee.appliedAt))))
      .filter((day) => day > paidAt)
      .sort();
    return later.length > 0 ? later[0].split('-').reverse().join('/') : null;
  })();

  const shownExcess = serverExcess ? parseFloat(serverExcess.excess) : excessCents / 100;
  const primaryLabel = mutation.isPending
    ? isEdit
      ? 'Saving...'
      : 'Recording...'
    : isEdit
      ? 'Save Changes'
      : step === 'excess'
        ? 'Confirm'
        : 'Record Payment';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Payment' : 'Record Payment'}</DialogTitle>
      <DialogContent>
        {step === 'excess' && !isEdit ? (
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(shownExcess)} more than the balance. What should happen to the extra?
            </Typography>
            {serverExcess && (
              <Alert severity="info">
                The balance changed since this window opened: {formatCurrency(serverExcess.remaining)} is now owing.
              </Alert>
            )}
            <FormControl>
              <RadioGroup
                value={excessAction}
                onChange={(e) => setExcessAction(e.target.value as 'CREDIT' | 'REFUND_DUE')}
                aria-label="What should happen to the extra"
              >
                <FormControlLabel value="CREDIT" control={<Radio />} label="Credit the family" />
                <FormHelperText sx={{ mt: -0.5, mb: 1, ml: 4 }}>
                  Comes off any invoice they have open now, oldest first. The rest comes off their next invoice.
                </FormHelperText>
                {mayRefund && <FormControlLabel value="REFUND_DUE" control={<Radio />} label="Refund due" />}
              </RadioGroup>
            </FormControl>
            {excessAction === 'REFUND_DUE' && (
              <TextField
                fullWidth
                label="Note for the refund (optional)"
                value={excessNote}
                onChange={(e) => setExcessNote(e.target.value)}
                inputProps={{ maxLength: 500 }}
              />
            )}
            {mutation.isError && (
              <Alert severity="error">{apiErrorMessage(mutation.error, 'Failed to record payment')}</Alert>
            )}
          </Box>
        ) : (
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {invoice && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#FCF6E6',
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Invoice Details
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {invoice.invoiceNumber} - {invoice.Family.name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 1,
                  }}
                >
                  <Typography variant="body2">Total:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatCurrency(invoice.total)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Already Paid:</Typography>
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    {formatCurrency(invoice.amountPaid)}
                  </Typography>
                </Box>
                {!isEdit && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      pt: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Remaining Balance:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {formatCurrency(remainingCents / 100)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Amount (read-only when amending, #642 D4) */}
            <TextField
              fullWidth
              required
              type="number"
              label="Payment Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={isEdit}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
              error={!isEdit && amount !== '' && !isAmountValid}
              helperText={!isEdit && amount !== '' && !isAmountValid ? 'Enter an amount above $0.00' : undefined}
            />
            {!isEdit && isAmountValid && shortCents > 0 && (
              <Alert severity="info">This leaves {formatCurrency(shortCents / 100)} owing.</Alert>
            )}
            {!isEdit && excessCents > 0 && (
              <Alert severity="warning">
                {formatCurrency(excessCents / 100)} more than the balance. You will choose what happens to the extra.
              </Alert>
            )}

            {/* Payment Date */}
            <TextField
              fullWidth
              required
              type="date"
              label="Date of Payment"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              disabled={isEdit}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayKey }}
              error={!isEdit && paidAt !== '' && !isDateValid}
              helperText={
                !isEdit && paidAt !== '' && !isDateValid
                  ? 'Payment date cannot be in the future'
                  : 'The day the money was received'
              }
            />
            {/* #372: a payment dated before a fee was added. Non-blocking: the fee stays unless waived. */}
            {!isEdit && lateFeeAfterPayment && (
              <Alert severity="info">
                This payment is dated before the late fee was added on {lateFeeAfterPayment}. If the family paid on
                time, waive the fee on the invoice page.
              </Alert>
            )}

            {/* Payment Method */}
            <FormControl fullWidth required disabled={isEdit}>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={method}
                label="Payment Method"
                onChange={(e: SelectChangeEvent) => setMethod(e.target.value as RecordableMethod)}
              >
                {PAYMENT_METHODS.map((pm) => (
                  <MenuItem key={pm.value} value={pm.value}>
                    {pm.label}
                  </MenuItem>
                ))}
                {isEdit && !PAYMENT_METHODS.some((pm) => pm.value === method) && (
                  <MenuItem value={method}>{PAYMENT_METHOD_LABELS[method] ?? method}</MenuItem>
                )}
              </Select>
            </FormControl>
            {isEdit && (
              <Typography variant="caption" color="text.secondary">
                To change the amount, method or date, void this payment and record it again.
              </Typography>
            )}

            {/* Reference */}
            <TextField
              fullWidth
              label="Reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={
                method === 'BANK_TRANSFER'
                  ? 'Bank transfer reference'
                  : method === 'CHECK'
                    ? 'Cheque number'
                    : 'Reference number'
              }
              helperText="Optional: Bank transfer ID, cheque number, etc."
            />

            {/* Notes */}
            <TextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              placeholder="Optional notes about this payment"
            />

            {isEdit && payment && (
              <Typography variant="caption" color="text.secondary">
                Recorded {formatRecordedAt(payment.createdAt, timeZone)}. Saving corrects this record; no email is
                sent to the family.
              </Typography>
            )}

            {mutation.isError && (
              <Alert severity="error">
                {apiErrorMessage(mutation.error, isEdit ? 'Failed to update payment' : 'Failed to record payment')}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={step === 'excess' && !isEdit ? () => { setStep('form'); setServerExcess(null); } : handleClose}
          disabled={mutation.isPending}
        >
          {step === 'excess' && !isEdit ? 'Back' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handlePrimary}
          disabled={(!isEdit && (!isAmountValid || !isDateValid)) || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
